import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, AlertCircle, Shield, Upload, FileText, Clock,
  ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle,
  ExternalLink, RefreshCw, Lock, Info, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Full agreement text for reference modal.
// DP64 Part 3c — {{designer}} is resolved to the tenant's own firm name at
// render (renderBullet). It was a hardcoded firm name.
const FULL_AGREEMENT_TEXT = `CONTRACTOR'S AGREEMENT

This document is a binding contract that will serve as a blanket agreement for and between {{designer}}, herein known as Designer, who is the authorized representative of the Client, and the undersigned, referred to, hereafter, as the Contractor. By signing, the Contractor and Designer agree to the terms set forth herein. This agreement shall remain in force from the date hereof and from year to year unless a change is agreed to in writing by both the Designer and the Contractor.

The parties agree to the following:

1. GENERAL PERFORMANCE
All Work of the Contractor will be performed in a good and workmanlike manner in accordance with the plans and specifications for each job and must comply with all Federal and State laws, codes, and regulations and all county and/or municipal ordinances and regulations effective where the work is to be performed under this contract. The contractor is to pay all permits, fees, taxes, and expenses connected with such compliance.

2. INDEPENDENT CONTRACTOR
The Designer and Contractor agree that the Contractor is being hired solely as an Independent Contractor and that neither the Contractor nor his employees shall be deemed to be employees of the Designer or the Client.

3. TIME
The Contractor agrees to begin work promptly upon receiving notification from the Designer. The Contractor will complete the work in a professional and workmanlike manner within a reasonable period of time after starting. Additionally, all work must be completed by the deadlines set by the Designer in writing. The contractor shall cooperate with other trades who may also be on the job site so that each reasonably may complete their respective work within the required time frames, and the Contractor shall, in any event, complete the Contractor's work within a time that will allow any other trades whose work depends on the completion of Contractor's work to also timely complete its work. At all times, the Contractor shall provide competent supervision, a sufficient number of skilled workers, and adequate and proper materials to maintain an efficient work schedule.

4. EXTRAS
No deviations from the work specified in the contract will be permitted or paid for by the Client unless a written extra work or change order is first agreed upon and signed as required.

5. ASSIGNMENT
No assignment of this Contractor agreement by the Contractor is permitted without prior review of the Designer and written permission from the Client.

6. HOLD HARMLESS
The Contractor agrees to protect, defend, and indemnify the Designer against and hold the Designer harmless for any and all claims, demands, liabilities, losses, expenses, suits, and actions (including attorney's fees) for or on account of any injury to any person, or any death at any time resulting from such injury, or any damage to any property, which may arise (or which may be alleged to have arisen) out of or in connection with the work covered by this contract.

7. MECHANICS LIEN
The Contractor shall furnish all partial and final lien waivers and release and sworn statements under the Missouri State Mechanic's Lien Law for the Contractor, and all Contractor's materials, men, and suppliers in a form satisfactory and acceptable to the Designer on behalf of the Client as a condition precedent to partial and final payments to Contractor hereunder.

8. CLEAN-UP
The contractor agrees to clean up all debris, trash, and refuse generated by his own trade at the end of each day and deposit it into the trash bin provided. The Contractor agrees to leave the job broom clean for the next trade.

9. DEFAULT
If the Contractor defaults in the performance of any of his duties or obligations hereunder and such default continues after verbal or written notice, the Designer may immediately terminate this Agreement.

10. CARE OF MATERIALS
The Contractor agrees to be diligent in the proper care of all materials, including materials supplied by the Designer or any other source. All usable materials are to be stored in an orderly way that protects them from wind and moisture and provides general site safety.

11. PAYMENT
The Designer, on behalf of the Client, shall provide stated time frames for the Contractor to submit invoices for the work performed. Invoices not received by the stated time will be processed and paid in the next pay period. Invoices in question will be held in their entirety until the disputed charge is resolved.

12. INSURANCE
The Contractor shall provide the Designer at the time of the signing of this agreement with a Certificate of Insurance, showing the following insurance during the period of the contract:

A. Commercial General Liability coverage with an insurance carrier rated A- or better by A.M. Best with limits equal to or exceeding:
- $500,000 Combined Single Limit for each occurrence
- $1,000,000 General Aggregate
- $1,000,000 Products/Completed Operations Aggregate
The Designer is to be named as Additional Insured on the Contractor's policy.

B. Worker's Compensation Insurance covering all persons performing work at the Client's job sites.

C. Automobile Liability Insurance for a minimum coverage amount of $500,000 each accident.

D. Architects, Engineers, and other Professional Vendors shall provide a minimum coverage of $500,000 each claim.

13. HEALTH AND SAFETY
The Contractor agrees to exercise all precautions necessary to prevent accidents. The Contractor will comply with all specific health and safety requirements of the Federal Occupational Safety and Health Act, Missouri Occupational Safety and Health Act, and any other applicable authority.

14. CONDUCT
The Contractor agrees that he, employees, and agents shall conduct themselves in a professional manner at all times. Contractor shall not use or be under the influence of alcoholic beverages or drugs on the job site. The Contractor agrees to not enter into any additional agreements with the Client or adjacent properties without consulting the Designer.

15. MAINTENANCE OF EROSION CONTROL
Contractor agrees that he, his employees, and all agents shall not disturb any erosion control systems constructed on site. If any silt fencing or hay bales are moved to gain access to the site, then its structures shall be returned to their effective status immediately.

16. ARBITRATION
This Agreement shall be governed by the laws of the State of Missouri. Any dispute shall be filed in the Circuit Court of Greene County, Missouri. WAIVER OF RIGHT TO JURY TRIAL: The Contractor hereby specifically waives any right to a jury trial.

17. WARRANTY
The Contractor shall warrant against any defects in workmanship and/or materials supplied by the Contractor for a period of one year from the date of installation.`;


export default function ContractorAgreement({ contractor, isAdminView = false, onStatusChange }) {
  const [agreement, setAgreement] = useState(null);
  const [cards, setCards] = useState([]);
  const [insuranceInfo, setInsuranceInfo] = useState({});
  const [status, setStatus] = useState('not_started');
  const [loading, setLoading] = useState(true);
  // DP64 Part 3 — the DESIGNER's firm name, from the tenant's own settings.
  // Blank when the tenant has none. NEVER another firm's, and never a literal:
  // a contractor of one firm must not read that they are agreeing to work for
  // another. Before DP64 this file named one firm eight times.
  const [firmName, setFirmName] = useState('');

  // Signature fields
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signing, setSigning] = useState(false);

  // Insurance uploads
  const [uploadingCoi, setUploadingCoi] = useState(false);
  const [uploadingWc, setUploadingWc] = useState(false);

  // Admin verification
  const [verifying, setVerifying] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // Full agreement modal
  const [showFullAgreement, setShowFullAgreement] = useState(false);

  // Expanded cards
  const [expandedCard, setExpandedCard] = useState(null);

  const companyName = contractor?.company_name || 'your company';

  const fetchAgreement = useCallback(async () => {
    if (!contractor?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contractor-portal/${contractor.id}/agreement`);
      setAgreement(res.data.agreement);
      setCards(res.data.cards || []);
      setInsuranceInfo(res.data.insurance_info || {});
      setFirmName(res.data.firm_name || '');
      setStatus(res.data.status || 'not_started');
    } catch (err) {
      console.error('Error fetching agreement:', err);
    } finally {
      setLoading(false);
    }
  }, [contractor?.id]);

  useEffect(() => {
    fetchAgreement();
  }, [fetchAgreement]);

  const acceptedCards = new Set(agreement?.accepted_cards || []);
  const totalCards = cards.length;
  const checkedCount = acceptedCards.size;
  const allChecked = totalCards > 0 && checkedCount === totalCards;
  const progressPercent = totalCards > 0 ? Math.round((checkedCount / totalCards) * 100) : 0;

  const handleToggleCard = async (cardId) => {
    if (status === 'active' || status === 'signed') return; // Can't change after signing

    const isAccepted = acceptedCards.has(cardId);
    try {
      const res = await axios.post(`${API}/contractor-portal/${contractor.id}/agreement/accept-card`, {
        card_id: cardId,
        accepted: !isAccepted,
      });
      setAgreement(res.data.agreement);
    } catch (err) {
      console.error('Error toggling card:', err);
      toast.error('Failed to update');
    }
  };

  const handleSign = async () => {
    if (!signerName.trim() || !signerTitle.trim()) {
      toast.error('Name and Title are required');
      return;
    }
    if (!allChecked) {
      toast.error('Please accept all agreement cards before signing');
      return;
    }

    setSigning(true);
    try {
      const res = await axios.post(`${API}/contractor-portal/${contractor.id}/agreement/sign`, {
        signer_name: signerName,
        signer_title: signerTitle,
      });
      setAgreement(res.data.agreement);
      setStatus('signed');
      toast.success('Agreement signed successfully!');
      onStatusChange?.('signed');
    } catch (err) {
      console.error('Error signing:', err);
      toast.error(err.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const handleUploadInsurance = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const setter = docType === 'coi' ? setUploadingCoi : setUploadingWc;
    setter(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(
        `${API}/contractor-portal/${contractor.id}/agreement/upload-insurance?doc_type=${docType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setAgreement(res.data.agreement);
      toast.success(`${docType === 'coi' ? 'Certificate of Insurance' : "Workers' Comp Certificate"} uploaded`);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed');
    } finally {
      setter(false);
    }
  };

  const handleVerify = async (verified) => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/contractor-portal/${contractor.id}/agreement/verify`, {
        verified,
        notes: adminNotes,
      });
      setAgreement(res.data.agreement);
      setStatus(verified ? 'active' : 'rejected');
      toast.success(verified ? 'Contractor approved!' : 'Agreement rejected');
      onStatusChange?.(verified ? 'active' : 'rejected');
    } catch (err) {
      console.error('Verify error:', err);
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleRenew = async () => {
    try {
      const res = await axios.post(`${API}/contractor-portal/${contractor.id}/agreement/renew`, {});
      setAgreement(res.data.agreement);
      setStatus('in_progress');
      toast.success('Agreement renewal started');
      onStatusChange?.('in_progress');
    } catch (err) {
      console.error('Renew error:', err);
      toast.error('Failed to start renewal');
    }
  };

  // DP64 Part 3 — this line replaced one firm's name with the same string.
  // It looked like tenant substitution and had never substituted anything. It
  // now puts the tenant's own firm name in, and a blank where they have none.
  const renderBullet = (text) => {
    return text
      .replace(/\{\{designer\}\}/g, firmName)
      .replace(/[ \t]{2,}/g, ' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="agreement-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  // ============ STATUS BANNERS ============

  const StatusBanner = () => {
    if (status === 'active') {
      return (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6" data-testid="agreement-status-active">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-400">Agreement Active</p>
              <p className="text-sm text-green-400/70">
                Signed by {agreement?.signer_name} on {new Date(agreement?.signed_at).toLocaleDateString()}.
                {agreement?.expires_at && ` Expires ${new Date(agreement.expires_at).toLocaleDateString()}.`}
              </p>
            </div>
          </div>
        </div>
      );
    }
    if (status === 'signed') {
      return (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6" data-testid="agreement-status-signed">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-blue-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-400">Agreement Signed — Pending Verification</p>
              <p className="text-sm text-blue-400/70">
                Signed by {agreement?.signer_name}. Insurance documents are being reviewed{firmName ? ` by the ${firmName} team` : ''}.
              </p>
            </div>
          </div>
        </div>
      );
    }
    if (status === 'expired') {
      return (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6" data-testid="agreement-status-expired">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-400">Agreement Expired — Renewal Required</p>
              <p className="text-sm text-amber-400/70">
                Your agreement has expired. Annual re-approval is required to continue working on{firmName ? ` ${firmName}` : ''} projects.
              </p>
            </div>
            {!isAdminView && (
              <Button onClick={handleRenew} size="sm" className="ml-auto bg-amber-600 hover:bg-amber-700 text-white">
                <RefreshCw className="w-4 h-4 mr-1" /> Renew
              </Button>
            )}
          </div>
        </div>
      );
    }
    if (status === 'rejected') {
      return (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6" data-testid="agreement-status-rejected">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-400">Agreement Rejected</p>
              <p className="text-sm text-red-400/70">
                Your agreement was not approved. Please contact{firmName ? ` ${firmName}` : ' us'} for details.
                {agreement?.admin_notes && ` Notes: ${agreement.admin_notes}`}
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const canEdit = status === 'not_started' || status === 'in_progress';

  return (
    <div className="space-y-6" data-testid="contractor-agreement">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#E5E2DD]" data-testid="agreement-title">
            Subcontractor Agreement
          </h2>
          <p className="text-sm text-[#8A8A8A] mt-1">
            This agreement governs all work you perform{firmName ? ` for ${firmName}` : ''}. You must agree to these terms before being assigned to any project.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdminView && (
            <a
              href={`/documents/contractor-agreement/new?contractor=${contractor?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#2A2A2A] text-sm text-[#8A8A8A] hover:text-[#E5E2DD] hover:border-[#3A3A3A] transition-colors"
              data-testid="edit-agreement-btn"
            >
              <Pencil className="w-4 h-4" />
              Edit Agreement
            </a>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFullAgreement(true)}
            className="border-[#2A2A2A] text-[#8A8A8A] hover:text-[#E5E2DD]"
            data-testid="view-full-agreement-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            Full Agreement
          </Button>
        </div>
      </div>

      <StatusBanner />

      {/* Signature Record — visible after signing */}
      {(status === 'active' || status === 'signed') && agreement?.signer_name && (
        <Card className="bg-[#0D0D0D] border-[#2A2A2A]" data-testid="signature-record">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#70170F]/20 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-[#BC5D41]" />
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-[#5A5A5A] mb-0.5">Signed By</p>
                  <p className="text-sm text-[#E5E2DD] font-medium" style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic' }}>
                    {agreement.signer_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5A5A] mb-0.5">Title</p>
                  <p className="text-sm text-[#E5E2DD]">{agreement.signer_title}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5A5A] mb-0.5">Date Signed</p>
                  <p className="text-sm text-[#E5E2DD]">{new Date(agreement.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
            </div>
            {agreement.expires_at && (
              <div className="mt-3 pt-3 border-t border-[#2A2A2A] flex items-center gap-2 text-xs text-[#5A5A5A]">
                <Clock className="w-3 h-3" />
                <span>Agreement valid through {new Date(agreement.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. Annual renewal required.</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Progress Bar */}
      {canEdit && (
        <div className="space-y-2" data-testid="agreement-progress">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#8A8A8A]">{checkedCount} of {totalCards} sections accepted</span>
            <span className="text-[#BC5D41] font-medium">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-[#2A2A2A]" />
        </div>
      )}

      {/* Agreement Cards */}
      <div className="space-y-3" data-testid="agreement-cards">
        {cards.map((card, index) => {
          const isChecked = acceptedCards.has(card.id);
          const isExpanded = expandedCard === card.id;
          const isDisabled = !canEdit;

          return (
            <div
              key={card.id}
              className={`rounded-xl border transition-all ${
                isChecked
                  ? 'bg-green-500/5 border-green-500/30'
                  : 'bg-[#0D0D0D] border-[#2A2A2A] hover:border-[#3A3A3A]'
              } ${isDisabled ? 'opacity-70' : ''}`}
              data-testid={`agreement-card-${card.id}`}
            >
              <div
                className="flex items-start gap-4 p-4 cursor-pointer"
                onClick={() => setExpandedCard(isExpanded ? null : card.id)}
              >
                {/* Checkbox */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) handleToggleCard(card.id);
                  }}
                  disabled={isDisabled}
                  className={`mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isChecked
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-[#5A5A5A] hover:border-[#BC5D41]'
                  } ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`agreement-checkbox-${card.id}`}
                >
                  {isChecked && <Check className="w-4 h-4" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5A5A5A] font-mono">{String(index + 1).padStart(2, '0')}</span>
                    <h3 className={`font-medium ${isChecked ? 'text-green-400' : 'text-[#E5E2DD]'}`}>
                      {card.title}
                    </h3>
                  </div>
                  {/* Always show bullets */}
                  <ul className="mt-2 space-y-1.5">
                    {card.bullets.map((bullet, bi) => (
                      <li key={bi} className="text-sm text-[#8A8A8A] flex gap-2">
                        <span className="text-[#5A5A5A] mt-1 flex-shrink-0">-</span>
                        <span>{renderBullet(bullet)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {isChecked ? (
                  <Badge className="bg-green-500/20 text-green-400 text-xs flex-shrink-0">
                    <Check className="w-3 h-3 mr-1" /> Agreed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-[#5A5A5A] border-[#3A3A3A] flex-shrink-0">
                    Pending
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Insurance Documents Section */}
      <Card className="bg-[#0D0D0D] border-[#2A2A2A]" data-testid="insurance-upload-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-[#E5E2DD] text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#BC5D41]" />
            Insurance Documents
          </CardTitle>
          <CardDescription className="text-[#8A8A8A]">
            {canEdit
              ? 'Upload your Certificate of Insurance (COI) and Workers\' Compensation Certificate'
              : 'Your insurance documents on file. You can update them at any time.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Where to send insurance info */}
          {(insuranceInfo?.email || insuranceInfo?.address) && (
            <div className="bg-[#1A1A1A] rounded-lg p-3 border border-[#2A2A2A]" data-testid="insurance-send-info">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-[#E5E2DD] font-medium mb-1">Send Insurance Documents To:</p>
                  {insuranceInfo.attention && (
                    <p className="text-[#8A8A8A]">Attn: {insuranceInfo.attention}</p>
                  )}
                  {insuranceInfo.email && (
                    <p className="text-[#8A8A8A]">
                      Email: <a href={`mailto:${insuranceInfo.email}`} className="text-[#BC5D41] hover:underline">{insuranceInfo.email}</a>
                    </p>
                  )}
                  {insuranceInfo.address && (
                    <p className="text-[#8A8A8A]">Address: {insuranceInfo.address}</p>
                  )}
                  {insuranceInfo.notes && (
                    <p className="text-[#5A5A5A] mt-1 text-xs">{insuranceInfo.notes}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Verification status */}
          {agreement?.insurance_verified && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">
                Insurance verified on {new Date(agreement.insurance_verified_at).toLocaleDateString()}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* COI */}
            <div className="space-y-2">
              <Label className="text-[#E5E2DD] text-sm">Certificate of Insurance (COI)</Label>
              {agreement?.insurance_docs?.coi ? (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-green-400 truncate flex-1">{agreement.insurance_docs.coi.filename}</span>
                    {agreement.insurance_docs.coi.url && (
                      <a href={agreement.insurance_docs.coi.url} target="_blank" rel="noopener noreferrer" data-testid="view-coi-link">
                        <ExternalLink className="w-4 h-4 text-green-400 hover:text-green-300" />
                      </a>
                    )}
                  </div>
                  {agreement.insurance_docs.coi.uploaded_at && (
                    <p className="text-xs text-[#5A5A5A]">Uploaded {new Date(agreement.insurance_docs.coi.uploaded_at).toLocaleDateString()}</p>
                  )}
                  {/* Allow re-upload */}
                  <label className="inline-flex items-center gap-1 text-xs text-[#BC5D41] hover:text-[#E5E2DD] cursor-pointer" data-testid="replace-coi">
                    <Upload className="w-3 h-3" /> Replace
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleUploadInsurance(e, 'coi')} />
                  </label>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-[#2A2A2A] hover:border-[#BC5D41] cursor-pointer transition-colors" data-testid="upload-coi">
                  {uploadingCoi ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#BC5D41]" />
                  ) : (
                    <Upload className="w-5 h-5 text-[#5A5A5A]" />
                  )}
                  <span className="text-sm text-[#8A8A8A]">
                    {uploadingCoi ? 'Uploading...' : 'Upload COI'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleUploadInsurance(e, 'coi')}
                    disabled={uploadingCoi}
                  />
                </label>
              )}
            </div>

            {/* Workers Comp */}
            <div className="space-y-2">
              <Label className="text-[#E5E2DD] text-sm">Workers' Compensation Certificate</Label>
              {agreement?.insurance_docs?.workers_comp ? (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-green-400 truncate flex-1">{agreement.insurance_docs.workers_comp.filename}</span>
                    {agreement.insurance_docs.workers_comp.url && (
                      <a href={agreement.insurance_docs.workers_comp.url} target="_blank" rel="noopener noreferrer" data-testid="view-workers-comp-link">
                        <ExternalLink className="w-4 h-4 text-green-400 hover:text-green-300" />
                      </a>
                    )}
                  </div>
                  {agreement.insurance_docs.workers_comp.uploaded_at && (
                    <p className="text-xs text-[#5A5A5A]">Uploaded {new Date(agreement.insurance_docs.workers_comp.uploaded_at).toLocaleDateString()}</p>
                  )}
                  <label className="inline-flex items-center gap-1 text-xs text-[#BC5D41] hover:text-[#E5E2DD] cursor-pointer" data-testid="replace-workers-comp">
                    <Upload className="w-3 h-3" /> Replace
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleUploadInsurance(e, 'workers_comp')} />
                  </label>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-[#2A2A2A] hover:border-[#BC5D41] cursor-pointer transition-colors" data-testid="upload-workers-comp">
                  {uploadingWc ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#BC5D41]" />
                  ) : (
                    <Upload className="w-5 h-5 text-[#5A5A5A]" />
                  )}
                  <span className="text-sm text-[#8A8A8A]">
                    {uploadingWc ? 'Uploading...' : "Upload Workers' Comp"}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleUploadInsurance(e, 'workers_comp')}
                    disabled={uploadingWc}
                  />
                </label>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signature Section */}
      {canEdit && allChecked && (
        <Card className="bg-[#0D0D0D] border-[#2A2A2A]" data-testid="signature-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#E5E2DD] text-base">Final Acknowledgment & Signature</CardTitle>
            <CardDescription className="text-[#8A8A8A]">
              I confirm that I have reviewed the full{firmName ? ` ${firmName}` : ''} Subcontractor Agreement and that all of the above statements are true and accurate for my business. I understand this agreement applies as a blanket agreement to all{firmName ? ` ${firmName}` : ''} projects I work on.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#E5E2DD] text-sm">Name (type-to-sign) *</Label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Your full name"
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD] placeholder:text-[#5A5A5A]"
                  data-testid="signer-name-input"
                />
              </div>
              <div>
                <Label className="text-[#E5E2DD] text-sm">Title *</Label>
                <Input
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  placeholder="e.g., Owner, Project Manager"
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD] placeholder:text-[#5A5A5A]"
                  data-testid="signer-title-input"
                />
              </div>
            </div>
            <div className="text-xs text-[#5A5A5A]">
              Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <Button
              onClick={handleSign}
              disabled={signing || !signerName.trim() || !signerTitle.trim()}
              className="w-full bg-[#70170F] hover:bg-[#8B1D12] text-white h-12 text-base"
              data-testid="sign-agreement-btn"
            >
              {signing ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Lock className="w-5 h-5 mr-2" />
              )}
              Sign and Complete Agreement
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Admin Verification Section */}
      {isAdminView && status === 'signed' && (
        <Card className="bg-amber-500/5 border-amber-500/30" data-testid="admin-verify-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-400 text-base flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Admin: Verify & Approve
            </CardTitle>
            <CardDescription className="text-amber-400/70">
              Review the contractor's insurance documents and agreement before approval.
              The contractor cannot be assigned to projects until verified.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show uploaded docs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A]">
                <p className="text-xs text-[#5A5A5A] mb-1">COI</p>
                {agreement?.insurance_docs?.coi ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-400 truncate">{agreement.insurance_docs.coi.filename}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-400">Not uploaded</span>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A]">
                <p className="text-xs text-[#5A5A5A] mb-1">Workers' Comp</p>
                {agreement?.insurance_docs?.workers_comp ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-400 truncate">{agreement.insurance_docs.workers_comp.filename}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-400">Not uploaded</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-[#E5E2DD] text-sm">Admin Notes (optional)</Label>
              <Input
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this contractor..."
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD] placeholder:text-[#5A5A5A]"
                data-testid="admin-notes-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => handleVerify(true)}
                disabled={verifying}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="admin-approve-btn"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Approve Contractor
              </Button>
              <Button
                onClick={() => handleVerify(false)}
                disabled={verifying}
                variant="outline"
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="admin-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: Renew expired agreement */}
      {isAdminView && status === 'expired' && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-amber-400 font-medium">Agreement Expired</p>
              <p className="text-sm text-amber-400/70">Initiate renewal for the contractor to re-sign.</p>
            </div>
            <Button onClick={handleRenew} className="bg-amber-600 hover:bg-amber-700 text-white">
              <RefreshCw className="w-4 h-4 mr-1" /> Initiate Renewal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Full Agreement Modal */}
      <Dialog open={showFullAgreement} onOpenChange={setShowFullAgreement}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-[#0D0D0D] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-[#E5E2DD]">
              {firmName ? `${firmName} Subcontractor Agreement` : 'Subcontractor Agreement'}
            </DialogTitle>
            <DialogDescription className="text-[#8A8A8A]">
              Full agreement text for reference
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-[#1A1A1A] rounded-lg p-6" data-testid="full-agreement-content">
            <pre className="whitespace-pre-wrap text-sm text-[#E5E2DD] font-sans leading-relaxed">
              {renderBullet(FULL_AGREEMENT_TEXT)}
            </pre>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[#2A2A2A] text-xs text-[#5A5A5A]">
            <span>KEY-MADE Platform</span>
            <div className="flex gap-3">
              <a href={`/legal/privacy?role=contractor&name=${encodeURIComponent(contractor?.contact_name || '')}&email=${encodeURIComponent(contractor?.email || '')}&contractor_id=${contractor?.id || ''}`} target="_blank" rel="noopener noreferrer" className="text-[#BC5D41] hover:underline">Privacy Policy</a>
              <a href={`/legal/terms?role=contractor&name=${encodeURIComponent(contractor?.contact_name || '')}&email=${encodeURIComponent(contractor?.email || '')}&contractor_id=${contractor?.id || ''}`} target="_blank" rel="noopener noreferrer" className="text-[#BC5D41] hover:underline">Terms of Service</a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
